import * as Yup from 'yup';
import { FieldRow, Form } from '@/components';
import { useField } from 'formik';
import { Col } from 'antd';
import moment, { Moment } from 'moment';

interface Props {
  id?: string;
  initialValue?: AnnouncementFormValues;
  onSubmit: (value: AnnouncementFormValues) => any;
}

const schema = Yup.object({
  title: Yup.string().required().max(250),
  body: Yup.string().required(),
  startDate: Yup.date()
    .nullable()
    .required()
    .when('endDate', (endDate, schema) => {
      return endDate
        ? schema.max(endDate, {
            key: 'activities_start_date_must_be_before_end_date',
          })
        : schema;
    }),
  endDate: Yup.date()
    .min(Yup.ref('startDate'), {
      key: 'activities_end_date_must_be_after_start_date',
    })
    .nullable(),
  attachmentIds: Yup.array()
    .of(Yup.string().nullable().required())
    .nullable()
    .defined(),
});

export type AnnouncementFormValues = Omit<
  Yup.InferType<typeof schema>,
  'startDate' | 'endDate'
> & {
  startDate: Moment;
  endDate: Moment | null;
};

const DEFAULT_VALUE: AnnouncementFormValues = {
  title: '',
  body: '',
  startDate: moment(),
  endDate: null,
  attachmentIds: [],
};

export function AnnouncementEditPanel(props: Props) {
  const { initialValue, onSubmit, id } = props;
  const value = initialValue ?? DEFAULT_VALUE;

  return (
    <Form.Formik<AnnouncementFormValues>
      uid="announcement-details"
      i18n="announcements.details"
      initialValues={{ ...value, attachmentIds: [] }}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      <Form.Input name="title" required />

      <FieldRow>
        <Col span={12}>
          <Form.Date name="startDate" required allowClear={false} />
        </Col>
        <Col span={12}>
          <Form.Date name="endDate" />
        </Col>
      </FieldRow>

      <FormRichText name="body" attachmentName="attachmentIds" ownerId={id} />
    </Form.Formik>
  );
}

function FormRichText({
  name,
  attachmentName,
  ownerId,
}: {
  name: string;
  attachmentName: string;
  ownerId?: string;
}) {
  const [, , { setValue }] = useField(attachmentName);
  return (
    <Form.RichTextEditor
      name={name}
      required
      setAttachmentIds={setValue}
      ownerId={ownerId ?? ''}
      ownerType="Announcement"
    />
  );
}
